import "./../areaChart.css";

import * as d3 from "d3";
import React, { useEffect } from "react";

interface SalaryDistributionLinePointsProps {
    xScale: d3.ScaleLinear<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    data: {
        percentile: number;
        salary: number;
    }[];
    circleRadius?: number;
    mobile: boolean;
    d3Id: string;
}

export const SalaryDistributionLinePoints = (
    props: SalaryDistributionLinePointsProps
) => {
    const { xScale, yScale, data, circleRadius = 2, mobile, d3Id } = props;

    const getBubbleWidth = (length: number) => {
        if (mobile) return length === 6 ? 136 : 124;

        return length === 6 ? 32 : 28;
    };

    // these handle conditions where the tooltip might appear off screen for high or low percentiles
    const getBubbleXPosition = (d: { percentile: number; salary: number }) => {
        if (d.percentile === 10 && mobile) return xScale(10);
        if (d.percentile === 90 && mobile) return xScale(70);
        return xScale(d.percentile) - (mobile ? 64 : 16);
    };

    // these handle conditions where the tooltip might appear off screen for high or low percentiles
    const getSalaryXPosition = (d: { percentile: number; salary: number }) => {
        if (d.percentile === 10 && mobile) return xScale(12);
        if (d.percentile === 90 && mobile) return xScale(72);
        return xScale(d.percentile) - (mobile ? 50 : 13);
    };

    useEffect(() => {
        const clickState = {
            "10": "hidden",
            "25": "hidden",
            "50": "hidden",
            "75": "hidden",
            "90": "hidden",
        };
        const bubbleContainer = d3.select(`#${d3Id}`).selectAll(".line-points");

        bubbleContainer
            .append("g")
            .selectAll("val")
            .data(data)
            .enter()
            .append("rect")
            .attr("class", d => `tooltip${d.percentile}`)
            .attr("width", d => getBubbleWidth(d.salary.toString().length))
            .attr("height", mobile ? 48 : 12)
            .attr("rx", 2)
            .attr("ry", 2)
            .style("fill", "white")
            .attr("stroke", "#54d2c7")
            .attr("stroke-width", mobile ? 2 : 0.5)
            .style("visibility", "hidden")
            .attr("y", d => yScale(d.salary) - (mobile ? 64 : 16))
            .attr("x", d => getBubbleXPosition(d));

        bubbleContainer
            .append("g")
            .selectAll("val")
            .data(data)
            .enter()
            .append("text")
            .html(d => d3.format("$,")(d.salary))
            .attr("class", d => `tooltip${d.percentile}`)
            .style("visibility", "hidden")
            .style("fill", "#54d2c7")
            .attr("font-size", mobile ? 24 : 6)
            .attr("font-weight", 200)
            .attr("y", d => yScale(d.salary) - (mobile ? 30 : 7.5))
            .attr("x", d => getSalaryXPosition(d));

        /* Add circles in the line */
        d3.select(`#${d3Id}`)
            .selectAll(".line-points")
            .selectAll("circle-group")
            .data([data])
            .enter()
            .append("g")
            .style("fill", "white")
            .selectAll("circle")
            .data((d, i) => d)
            .enter()
            .append("g")
            .append("circle")
            .attr("class", (d, i) => `circle${d.percentile}`)
            .attr("cx", d => xScale(d.percentile))
            .attr("cy", d => yScale(d.salary))
            .attr("r", mobile ? 8 : 2)
            .style("stroke", "#54d2c7")
            .attr("stroke-width", mobile ? 2 : 0.5)
            .on(
                "mouseleave",
                mobile
                    ? null
                    : d => {
                          d3.selectAll(`.circle${d.percentile}`)
                              .style("fill", "white")
                              .attr("r", circleRadius);
                          d3.selectAll(`.tooltip${d.percentile}`).style(
                              "visibility",
                              "hidden"
                          );
                      }
            )
            .on(
                "mouseover",
                mobile
                    ? null
                    : d => {
                          d3.selectAll(`.circle${d.percentile}`)
                              .style("fill", "#54d2c7")
                              .attr("r", circleRadius + 0.5);
                          d3.selectAll(`.tooltip${d.percentile}`).style(
                              "visibility",
                              "visible"
                          );
                      }
            )
            .on("click", d => {
                const percentileKey = d.percentile.toString();
                d3.selectAll(`.tooltip${d.percentile}`).style(
                    "visibility",
                    clickState[percentileKey] === "hidden"
                        ? "visible"
                        : "hidden"
                );

                if (clickState[percentileKey] === "hidden") {
                    clickState[percentileKey] = "visible";
                    d3.selectAll(`.circle${d.percentile}`).style(
                        "fill",
                        "#54d2c7"
                    );
                } else {
                    clickState[percentileKey] = "hidden";
                    d3.selectAll(`.circle${d.percentile}`).style(
                        "fill",
                        "white"
                    );
                }
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <g className="line-points"></g>;
};
